import React, { createContext, useEffect, useState } from 'react'
import { UserType } from 'src/lib/types/user'
import { getParsedUserObj } from 'src/lib/utils/user'
import { constants } from '../utils/constants'
import Cookies from 'js-cookie'
import { staticOptions } from '../core/api-client'
import useSWR from 'swr'
import { apiURI } from '../utils/constants'
import { fetcher } from '../core/api-client'
import { getActiveStore } from '../utils/onboarding'
import { useOnboarding } from './OnboardingContext'
import { useRouter } from 'next/router'
import { hasAuthCookie } from '../utils/helpers'

interface UserContextType {
  setUserCtx: (data: any) => void
  user: UserType | undefined
  canAccess: (value: string, quantity: number) => boolean
  hasAllTabPermissions: (tabName: string) => boolean
}

const UserContext = createContext({} as UserContextType)

const UserProvider = (props: any) => {
  const router = useRouter()
  const [user, setUser] = useState<UserType>()
  const { setActiveShop } = useOnboarding()

  const { data: userResponse, isValidating } = useSWR<any, any, [string, string | undefined] | null>(
    hasAuthCookie() ? ['/merchant-user/profile', apiURI.AUTH_ENGINE] : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  useEffect(() => {
    if (isValidating || !userResponse) return
    const { data } = userResponse as any
    const parsedData = getParsedUserObj(data)
    setUser(parsedData)
    const activeStore = getActiveStore(user?.organisations)
    if (Boolean(activeStore?.in_onboarding)) {
      setActiveShop(activeStore?.merchant_id)
      router.push('/onboarding')
    }
  }, [isValidating, userResponse])

  const setUserCtx = (data: any) => {
    const parsedData = getParsedUserObj(data)
    Cookies.set(
      constants.FLO_PROFILE_DATA,
      JSON.stringify({
        sortmentTrackingId: parsedData?.sortmentTrackingId
      }),
      {
        expires: constants.FLO_USER_COOKIE_EXPIRY,
        sameSite: 'none',
        secure: true
      }
    )
    setUser(parsedData)
  }

  const canAccess = (value: string, quantity = 0) => {
    if (!value) return false
    const hasPermission: any = user?.permissions?.find((permission: any) => permission.id === value)
    const limit = hasPermission?.metadata?.limit
    if (limit) {
      return Boolean(limit > quantity)
    }
    return Boolean(hasPermission)
  }

  const hasAllTabPermissions = (tabName: string) => {
    if (!tabName) return false

    const hasPermission = user?.permissions?.find((permission: any) => permission.id === tabName)

    if (!hasPermission) return false
    const totalTabPermissions = hasPermission.totalChildPermissions
    const availableTabPermissions = user?.permissions?.filter(
      (permission: any) => permission.parentPermissionId === tabName
    )
    return Boolean(totalTabPermissions === availableTabPermissions?.length)
  }

  const userContextValue: UserContextType = {
    setUserCtx,
    user,
    canAccess,
    hasAllTabPermissions
  }

  return <UserContext.Provider value={userContextValue} {...props} />
}

const useUser = () => React.useContext(UserContext)

export { UserProvider, useUser }
